import { clampTransitionDuration } from './transitions';
import type { EditorOverlayLayer, EditorTransition, TransitionType } from '@/types/editor';

export const LAYER_TRANSITION_TYPES = [
  'fade',
  'dissolve',
  'slideleft',
  'slideright',
  'slideup',
  'slidedown',
  'smoothleft',
  'smoothright',
] as const satisfies readonly TransitionType[];

const LAYER_TRANSITION_TYPE_SET = new Set<TransitionType>(LAYER_TRANSITION_TYPES);

export interface LayerTransitionVisual {
  opacityFactor: number;
  translateX: number;
  translateY: number;
  scale: number;
  clipPath?: string;
  filter?: string;
}

export function isLayerTransitionType(value: unknown): value is TransitionType {
  return typeof value === 'string' && LAYER_TRANSITION_TYPE_SET.has(value as TransitionType);
}

export function cleanLayerTransition(value: unknown): EditorTransition | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const candidate = value as Partial<EditorTransition>;
  if (!isLayerTransitionType(candidate.type)) return undefined;
  return {
    type: candidate.type,
    durationSec: clampTransitionDuration(Number(candidate.durationSec)),
  };
}

export function effectiveLayerTransitionDuration(
  layer: Pick<EditorOverlayLayer, 'start' | 'end'>,
  transition?: EditorTransition
): number {
  if (!transition) return 0;
  const layerDuration = Math.max(0, layer.end - layer.start);
  return Math.min(clampTransitionDuration(transition.durationSec), layerDuration / 2);
}

function visualAt(type: TransitionType, visibleProgress: number): LayerTransitionVisual {
  const p = Math.min(1, Math.max(0, visibleProgress));
  const hidden = 1 - p;
  const visual: LayerTransitionVisual = {
    opacityFactor: 1,
    translateX: 0,
    translateY: 0,
    scale: 1,
  };

  switch (type) {
    case 'fade':
      visual.opacityFactor = p;
      break;
    case 'dissolve':
      visual.opacityFactor = p;
      visual.scale = 0.96 + 0.04 * p;
      visual.filter = `blur(${(hidden * 5).toFixed(2)}px) contrast(${(1 + hidden * 0.22).toFixed(3)})`;
      break;
    case 'slideleft':
      visual.translateX = hidden * 120;
      break;
    case 'slideright':
      visual.translateX = hidden * -120;
      break;
    case 'slideup':
      visual.translateY = hidden * 120;
      break;
    case 'slidedown':
      visual.translateY = hidden * -120;
      break;
    case 'smoothleft':
      visual.opacityFactor = p;
      visual.translateX = hidden * 28;
      visual.filter = `blur(${(hidden * 4).toFixed(2)}px)`;
      break;
    case 'smoothright':
      visual.opacityFactor = p;
      visual.translateX = hidden * -28;
      visual.filter = `blur(${(hidden * 4).toFixed(2)}px)`;
      break;
    default:
      visual.opacityFactor = p;
  }

  return visual;
}

export function getLayerTransitionVisual(
  layer: Pick<EditorOverlayLayer, 'start' | 'end' | 'transitionIn' | 'transitionOut'>,
  currentTime: number
): LayerTransitionVisual {
  const inDuration = effectiveLayerTransitionDuration(layer, layer.transitionIn);
  if (layer.transitionIn && inDuration > 0 && currentTime < layer.start + inDuration) {
    return visualAt(layer.transitionIn.type, (currentTime - layer.start) / inDuration);
  }

  const outDuration = effectiveLayerTransitionDuration(layer, layer.transitionOut);
  if (layer.transitionOut && outDuration > 0 && currentTime > layer.end - outDuration) {
    return visualAt(layer.transitionOut.type, (layer.end - currentTime) / outDuration);
  }

  return visualAt('fade', 1);
}
